import styled, { keyframes } from 'styled-components';

export const TicketDetailsWrapper = styled.div`
  width: 100%;
  min-height: 100dvh;
  background: ${({ theme }) => theme.landing.background};
  transition: background 0.3s ease;
`;

export const TicketDetailsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 64px;
  padding: ${({ theme }) => theme.landingSpacing.xl};
  display: flex;
  flex-direction: column;
  height: calc(100dvh - 64px);

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    padding: ${({ theme }) => theme.landingSpacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    margin-top: 56px;
    height: calc(100dvh - 56px);
    padding: ${({ theme }) => theme.landingSpacing.sm};
    gap: ${({ theme }) => theme.landingSpacing.sm};
  }
`;

export const PageHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.md};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    align-items: center;
    justify-content: flex-end;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.md};
  margin-bottom: ${({ theme }) => theme.landingSpacing.xl};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    display: none;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  flex-wrap: wrap;
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 700;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};
`;

export const PageHeaderText = styled.div`
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    display: none;
  }
`;

export const BackButton = styled.button`
  flex-shrink: 0;
  height: 34px;
  padding: 0 ${({ theme }) => theme.landingSpacing.md};
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    border-color: ${({ theme }) => theme.landing.text};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    height: 32px;
    font-size: 12px;
    padding: 0 ${({ theme }) => theme.landingSpacing.sm};
  }
`;

/* ------------------------------------------------------------------ */
/* Conversation panel                                                  */
/* ------------------------------------------------------------------ */

export const PanelCard = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  }
`;

export const PanelHeader = styled.header`
  padding: ${({ theme }) => theme.landingSpacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.landing.cardBorder};
  background: ${({ theme }) => theme.landing.cardBg};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const HeaderTopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.md};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    gap: ${({ theme }) => theme.landingSpacing.sm};
  }
`;

export const HeaderActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
  }

  & > button {
    @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
      width: auto;
      flex: 1;
    }
  }
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.landingSpacing.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    gap: 6px;
  }
`;

export const TicketIdBadge = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono || theme.fonts.primary};
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.landing.textSecondary};
  background: ${({ theme }) => theme.landing.iconBg};
  padding: 2px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 11px;
  font-weight: 600;
  padding: 3px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.full};
  white-space: nowrap;
  color: ${({ $status }) =>
    $status === 'OPEN' ? '#047857' : $status === 'WAITING' ? '#b45309' : '#475569'};
  background: ${({ $status }) =>
    $status === 'OPEN' ? '#ecfdf5' : $status === 'WAITING' ? '#fffbeb' : '#f1f5f9'};
  border: 1px solid
    ${({ $status }) =>
      $status === 'OPEN' ? '#a7f3d0' : $status === 'WAITING' ? '#fde68a' : '#e2e8f0'};
`;

export const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
`;

export const CategoryBadge = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.landing.text};
  background: ${({ theme }) => theme.landing.iconBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  padding: 2px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  white-space: nowrap;
`;

export const UpdatedText = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 12px;
  color: ${({ theme }) => theme.landing.textSecondary};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 11px;
  }
`;

export const TicketTitle = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 700;
  font-size: 13px;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.landing.text};
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    display: none;
  }
`;

export const CloseTicketButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 ${({ theme }) => theme.landingSpacing.md};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.cardBg};
  color: ${({ theme }) => theme.landing.textSecondary};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: ${({ theme }) => theme.landing.danger};
    border-color: ${({ theme }) => theme.landing.dangerBorder};
    background: ${({ theme }) => theme.landing.dangerBg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    height: 30px;
    padding: 0 ${({ theme }) => theme.landingSpacing.sm};
    font-size: 11px;
  }
`;

export const MobileBackButton = styled.button`
  display: none;
  align-items: center;
  margin-right: 6px;
  gap: 6px;
  height: 32px;
  padding: 0 ${({ theme }) => theme.landingSpacing.md};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.cardBg};
  color: ${({ theme }) => theme.landing.textSecondary};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: ${({ theme }) => theme.landing.danger};
    border-color: ${({ theme }) => theme.landing.dangerBorder};
    background: ${({ theme }) => theme.landing.dangerBg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    display: inline-flex;
    height: 30px;
    padding: 0 ${({ theme }) => theme.landingSpacing.sm};
    font-size: 11px;
  }
`;

/* ------------------------------------------------------------------ */
/* Message thread                                                      */
/* ------------------------------------------------------------------ */

export const MessagesContainer = styled.div`
  flex: 1;
  min-height: 0;
  padding: ${({ theme }) => theme.landingSpacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.lg};
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-behavior: auto;
  background: ${({ theme }) => theme.landing.background};

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover {
    scrollbar-color: ${({ theme }) => theme.landing.cardBorder} transparent;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 999px;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.landing.cardBorder};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
    gap: ${({ theme }) => theme.landingSpacing.md};
    scrollbar-color: ${({ theme }) => theme.landing.cardBorder} transparent;

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.landing.cardBorder};
    }
  }
`;

export const MessageGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: ${({ $isQuery }) => ($isQuery ? 'flex-end' : 'flex-start')};
`;

export const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: ${({ $isQuery }) => ($isQuery ? '0 4px 0 0' : '0 0 0 4px')};
  flex-wrap: wrap;
`;

export const AuthorAvatar = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.avatarbg};
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.primary};
  flex-shrink: 0;
`;

export const AuthorLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

export const TimeText = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 11px;
  color: ${({ theme }) => theme.landing.textSecondary};
  white-space: nowrap;
`;

export const MessageBubble = styled.div`
  max-width: 640px;
  width: fit-content;
  padding: ${({ theme }) => theme.landingSpacing.md};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.sm};

  ${({ $isQuery, theme }) =>
    $isQuery
      ? `
        background: ${theme.colors.msgBubbleBg};
        color: ${theme.landing.text};
        border-top-right-radius: 4px;
      `
      : `
        background: ${theme.landing.cardBg};
        color: ${theme.landing.text};
        border: 1px solid ${theme.landing.cardBorder};
        border-top-left-radius: 4px;
      `}

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    max-width: 88%;
    padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const MessageText = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  line-height: 1.6;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 13.5px;
    line-height: 1.5;
  }
`;

/* ------------------------------------------------------------------ */
/* Attachments (sent messages — view only)                             */
/* ------------------------------------------------------------------ */

export const AttachmentsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  padding-top: ${({ theme }) => theme.landingSpacing.sm};
  border-top: 1px solid ${({ theme }) => theme.landing.cardBorder};
`;

export const AttachmentPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  border: 1px solid
    ${({ $isQuery, theme }) => ($isQuery ? 'rgba(0,0,0, 0.18)' : theme.landing.cardBorder)};
  background: ${({ theme }) => theme.landing.iconBg};
  color: ${({ theme }) => theme.landing.text};
  font-family: ${({ theme }) => theme.fonts.mono || theme.fonts.primary};
  font-size: 11px;
  cursor: pointer;
  transition: background 0.12s ease;
  max-width: 100%;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const AttachmentIcon = styled.svg`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

export const AttachmentName = styled.span`
  font-family: inherit;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    max-width: 100px;
  }
`;

export const AttachmentSize = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 10px;
  opacity: 0.7;
  flex-shrink: 0;
`;

/* ------------------------------------------------------------------ */
/* Spinner — used inline wherever an attachment action is in flight    */
/* ------------------------------------------------------------------ */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const SpinWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  animation: ${spin} 0.8s linear infinite;

  svg {
    width: 12px;
    height: 12px;
  }
`;

/* ------------------------------------------------------------------ */
/* Reply composer                                                       */
/* ------------------------------------------------------------------ */

export const ComposerWrapper = styled.div`
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 2;
  padding: ${({ theme }) => theme.landingSpacing.md};
  border-top: 1px solid ${({ theme }) => theme.landing.cardBorder};
  background: ${({ theme }) => theme.landing.cardBg};
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.sm};
    padding-bottom: max(${({ theme }) => theme.landingSpacing.sm}, env(safe-area-inset-bottom));
  }
`;

export const PendingAttachmentsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.landingSpacing.xs};
`;

export const PendingAttachmentPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 10px;
  border-radius: ${({ theme }) => theme.landingBorderRadius.full};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  background: ${({ theme }) => theme.landing.iconBg};
  color: ${({ theme }) => theme.landing.text};
  font-family: ${({ theme }) => theme.fonts.mono || theme.fonts.primary};
  font-size: 11px;
  max-width: 100%;
  opacity: ${({ $busy }) => ($busy ? 0.75 : 1)};
  transition: opacity 0.15s ease;
`;

export const PendingAttachmentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  border-radius: 50%;
  flex-shrink: 0;
  transition:
    background 0.12s ease,
    color 0.12s ease;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    background: ${({ theme }) => theme.landing.cardBorder};
    color: ${({ theme }) => theme.landing.text};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: transparent;
  }
`;

export const ComposerBox = styled.div`
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  background: ${({ theme }) => theme.landing.background};
  transition:
    border 0.15s ease,
    box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${({ theme }) => theme.landing.buttonBg};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.landing.iconBg};
  }
`;

export const ReplyTextarea = styled.textarea`
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  padding: 10px ${({ theme }) => theme.landingSpacing.md};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  line-height: 1.4;
  color: ${({ theme }) => theme.landing.text};
  overflow-y: hidden;
  transition: height 0.1s ease;

  &::placeholder {
    color: ${({ theme }) => theme.landing.textSecondary};
  }

  &:focus {
    outline: none;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: 8px ${({ theme }) => theme.landingSpacing.sm};
    font-size: 14px;
  }
`;

export const ComposerActionsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.landingSpacing.sm};
  border-top: 1px solid ${({ theme }) => theme.landing.cardBorder};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: 6px 8px;
  }
`;

export const AttachButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  transition:
    background 0.12s ease,
    color 0.12s ease;
  white-space: nowrap;

  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }

  &:hover {
    color: ${({ theme }) => theme.landing.text};
    background: ${({ theme }) => theme.landing.iconBg};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 0;
    padding: 6px;

    svg {
      width: 17px;
      height: 17px;
    }
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 ${({ theme }) => theme.landingSpacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #fff;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    transform 0.1s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${({ theme }) => theme.landing.buttonHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    height: 34px;
    padding: 0 ${({ theme }) => theme.landingSpacing.sm};
  }
`;

export const HelperText = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 11px;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    display: none;
  }
`;
